import {isPlatformBrowser} from '@angular/common';
import {ChangeDetectionStrategy, Component, ElementRef, Inject, OnInit, PLATFORM_ID, ViewChild} from '@angular/core';
import {DcpAuthService} from '@app/auth/services/dcp-auth.service';
import {DelegoPaymentErrorService} from '@app/payment/services/delego-rapidpay/error-handling/delego-payment-error.service';
import {DcpProductUtilitiesService} from '@app/product/services/dcp-product-utilities.service';
import {DcpCartDecoratorService} from '@app/services/dcp-cart-decorator.service';
import {TransactionTypeCode} from '@app/services/transactionTypes/transaction-type-code';
import {TransactionTypeService} from '@app/services/transactionTypes/transaction-type.service';
import {ConfigService, ConfiguratorUriParameterService, SupportDialogData, TransactionType} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {ActiveCartFacade} from '@spartacus/cart/base/root';
import {OrderEntry} from '@spartacus/cart/base/root/models/cart.model';
import {Product, User} from '@spartacus/core';
import {OrderDetailsService} from '@spartacus/order/components';
import {Order} from '@spartacus/order/root/model';
import {CurrentProductService, LaunchDialogService} from '@spartacus/storefront';
import {UserAccountFacade} from '@spartacus/user/account/root';
import {combineLatest, Observable} from 'rxjs';
import {first, map, mergeMap} from 'rxjs/operators';

const CATEGORY_L1_UPSELL = 'upsell';
const CATEGORY_L1_CUSTOMER = 'appcenterCustomer';
const CATEGORY_L1_DIGITAL_NATIVE = 'digitalNative';
const CATEGORY_L2_DIGITAL_NATIVE = 'digitalNativeCheckout';
const CHECKOUT_URLS = ['/cart', '/checkout', '/order-confirmation'];

export interface SupportFormCategory {
  categoryL1?: string;
  categoryL2?: string;
}

export enum SupportFormSearchParameters {
  CUSTOMER_ID = 'customerID',
  USER_IDENTITY = 'userIdentity',
  EMAIL_ADDRESS = 'emailAddress',
  SOURCE = 'source',
  CART_ID = 'cartID',
  ERROR_CODE = 'errorCode',
  ERROR_MESSAGE = 'errorMessage',
  CATEGORY_L1 = 'categoryL1',
  CATEGORY_L2 = 'categoryL2',
}

export const DIGITAL_NATIVE_CHECKOUT_SUPPORT_FORM_CATEGORY: SupportFormCategory = {
  categoryL1: CATEGORY_L1_DIGITAL_NATIVE,
  categoryL2: CATEGORY_L2_DIGITAL_NATIVE
};

export const UPSELL_CHECKOUT_SUPPORT_FORM_CATEGORY: SupportFormCategory = {
  categoryL1: CATEGORY_L1_UPSELL
};

export const PARTNER_PDP_SUPPORT_FORM_CATEGORY: SupportFormCategory = {
  categoryL1: CATEGORY_L1_CUSTOMER
};

export const SAP_PDP_SUPPORT_FORM_CATEGORY: SupportFormCategory = {
  categoryL1: CATEGORY_L1_DIGITAL_NATIVE,
};

@UntilDestroy()
@Component({
  selector: 'dcp-support-form',
  templateUrl: './support-form.component.html',
  styleUrls: ['./support-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SupportFormComponent implements OnInit {
  @ViewChild('iFrame') iframeComponent: ElementRef;
  readonly baseUrl: string = this.configService.getString('supportForm.baseUrl');

  user: User;
  activeCartID: string;
  errorCode: string;
  errorMessage: string;
  cartEntries: OrderEntry[] = [];
  private isProductDetailsPage: boolean;
  private isCartHasNetNewEntries: boolean;
  private isUserLoggedIn: boolean;
  private isSapProduct: boolean;
  private srcUrl: URL;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private userFacade: UserAccountFacade,
    private activeCartFacade: ActiveCartFacade,
    private dcpCartDecoratorService: DcpCartDecoratorService,
    private orderDetailsService: OrderDetailsService,
    private dcpAuthService: DcpAuthService,
    private configuratorUriParameterService: ConfiguratorUriParameterService,
    private configService: ConfigService,
    private currentProductService: CurrentProductService,
    private dcpProductUtilitiesService: DcpProductUtilitiesService,
    private transactionTypeService: TransactionTypeService,
    private launchDialogService: LaunchDialogService,
    private delegoPaymentErrorService: DelegoPaymentErrorService
  ) {
  }

  ngOnInit(): void {
    this.dcpAuthService.isUserLoggedIn()
      .pipe(first(), untilDestroyed(this))
      .subscribe((isUserLoggedIn: boolean) => {
        this.isUserLoggedIn = isUserLoggedIn;
      });
    this.currentProductService.getProduct().pipe(first(), untilDestroyed(this)).subscribe((product: Product) => {
      this.isProductDetailsPage = !!product;
      if (this.isProductDetailsPage) {
        this.isSapProduct = this.dcpProductUtilitiesService.isSapProduct(product);
      }
    });
    setTimeout(() => {
      this.iframeComponent.nativeElement.focus();
    }, 0);
  }

  getURL(): Observable<string> {
    return this.isUserLoggedIn ? this.prefillLoggedInUrl() : this.url;
  }

  get url(): Observable<string> {
    return this.launchDialogService.data$.pipe(map((data: SupportDialogData) => {
      if (data?.targetUrl && this.isValidUrl(data.targetUrl)) {
        const targetUrl = new URL(data.targetUrl);

        if (!targetUrl?.pathname?.includes('iframe')) {
          targetUrl.pathname = targetUrl.pathname.concat('/iframe');
        }

        return targetUrl.toString();
      }

      this.srcUrl = new URL(this.baseUrl);
      const supportFormCategory: SupportFormCategory = this.getSupportFormCategory();
      this.setSearchParam(SupportFormSearchParameters.CUSTOMER_ID, this.user?.orgUnit?.uid);
      this.setSearchParam(SupportFormSearchParameters.USER_IDENTITY, this.user?.scimUserId);
      this.setSearchParam(SupportFormSearchParameters.EMAIL_ADDRESS, this.user?.email);
      this.setSearchParam(SupportFormSearchParameters.SOURCE, decodeURIComponent(this.currentURL));
      this.setSearchParam(SupportFormSearchParameters.CART_ID, this.activeCartID);
      this.setSearchParam(SupportFormSearchParameters.ERROR_CODE, this.errorCode || data?.errorCode);
      this.setSearchParam(SupportFormSearchParameters.ERROR_MESSAGE, this.errorMessage || data?.errorMessage);
      this.setSearchParam(SupportFormSearchParameters.CATEGORY_L1, supportFormCategory?.categoryL1);
      this.setSearchParam(SupportFormSearchParameters.CATEGORY_L2, supportFormCategory?.categoryL2);

      const sourceUrl: URL = new URL(this.currentURL);
      sourceUrl.searchParams.forEach((value: string, key: string) => {
        if (!this.srcUrl.searchParams.has(key)) {
          this.srcUrl.searchParams.append(key, value);
        }
      });

      return this.srcUrl.toString();
    }));

  }

  get currentURL(): string {
    return isPlatformBrowser(this.platformId) ? window.location.href : undefined;
  }

  private isValidUrl(url: string) {
    try {
      return new URL(url);
    } catch (_) {
      return false;
    }
  }

  private setSearchParam(parameter: string, value: string): void {
    if (value) {
      this.srcUrl.searchParams.set(parameter, value);
    }
  }

  private prefillLoggedInUrl(): Observable<string> {
    return combineLatest([
      this.userFacade.get(),
      this.activeCartFacade.getActiveCartId(),
      this.dcpCartDecoratorService.priceDataSubject,
      this.activeCartFacade.getEntries(),
      this.orderDetailsService.getOrderDetails().pipe(map((order: Order) => order?.entries ?? [])),
      this.transactionTypeService.getTransactionTypes$(),
      this.delegoPaymentErrorService.getErrorCodeAndMessage(),
      this.launchDialogService.data$
    ]).pipe(
      mergeMap((
        [
          user,
          activeCartID,
          pd,
          entries,
          orderEntries,
          transactionTypes,
          errorAndMessage,
          data,
        ]
      ) => {
        this.user = user;
        this.activeCartID = activeCartID;
        if (!data?.errorCode?.length && !data?.errorMessage?.length) {
          if (pd?.price) {
            this.errorCode = pd.price.errorCode;
            this.errorMessage = pd.price.errorMessage;
          } else {
            this.errorCode = errorAndMessage?.errorCode;
            this.errorMessage = errorAndMessage?.errorMessage;
          }
        }
        this.cartEntries = entries?.length ? entries : orderEntries;
        this.isCartHasNetNewEntries = this.cartEntries.some((entry: OrderEntry) => {
          const entryTransactionType: TransactionType = transactionTypes.find(tt => tt.code === entry.transactionType);
          return this.transactionTypeService.isNetNewTransactionType(entryTransactionType);
        });
        return this.url;
      }),
    );
  }

  private getSupportFormCategory(): SupportFormCategory {
    const action: string = this.configuratorUriParameterService.getAction();
    return this.isProductDetailsPage ? this.getPdpSupportFormCategory() : this.getActionSupportFormCategory(action);
  }

  private getPdpSupportFormCategory(): SupportFormCategory {
    return this.isSapProduct ? SAP_PDP_SUPPORT_FORM_CATEGORY : PARTNER_PDP_SUPPORT_FORM_CATEGORY;
  }

  private getActionSupportFormCategory(action: string): SupportFormCategory {
    return this.getDigitalNativeAction(action) || this.getOrderUpsellAction(action);
  }

  private isOrderUpsellAction(action: string): boolean {
    return action?.includes(TransactionTypeCode.ORDER_UPSELL) || (
      this.isCheckoutUrl() && this.cartEntries.length && !this.isCartHasNetNewEntries);
  }

  private isDigitalNativeAction(action: string): boolean {
    return action?.includes(TransactionTypeCode.ORDER_NEW) || (this.isCheckoutUrl() && this.isCartHasNetNewEntries);
  }

  private getOrderUpsellAction(action: string): SupportFormCategory {
    return this.isOrderUpsellAction(action) ? UPSELL_CHECKOUT_SUPPORT_FORM_CATEGORY : undefined;
  }

  private getDigitalNativeAction(action: string): SupportFormCategory {
    return this.isDigitalNativeAction(action) ? DIGITAL_NATIVE_CHECKOUT_SUPPORT_FORM_CATEGORY : undefined;
  }

  private isCheckoutUrl(): boolean {
    return CHECKOUT_URLS.some((item: string) => this.currentURL?.includes(item));
  }
}
